/**
 * PraticheTable — F-006 Phase 5 (IMPL-P5-005).
 *
 * Virtualized 6-column table of pratiche, powered by `@tanstack/react-table`
 * (sorting headers) + `@tanstack/react-virtual` (off-screen rows are not
 * rendered to the DOM). Sort is client-side over the rows already loaded —
 * if the user scrolls past ~200 rows we show a banner suggesting filters to
 * keep things snappy.
 *
 * Accessibility:
 *  - `role="table"` + `aria-rowcount={rows.length}` on the wrapper so screen
 *    readers can announce total row count even though the DOM only contains
 *    the visible window.
 *  - `aria-sort` on each header cell mirrors the live sort state.
 *  - Each row carries `role="row"` + `aria-rowindex` (PraticaRow).
 */
import { useRef, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ArrowUpDown, AlertTriangle } from 'lucide-react';
import type { PraticaListItem } from '@app/shared';
import { useArchivio } from '../hooks/useArchivio';
import { COL_WIDTHS, PraticaRow } from './PraticaRow';

const PERF_WARNING_THRESHOLD = 200;
const ROW_HEIGHT = 48; // px — matches Tailwind py-3 + line-height

const columnHelper = createColumnHelper<PraticaListItem>();

const columns = [
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Data',
    enableSorting: true,
    size: 12,
  }),
  columnHelper.accessor('tipologia', {
    id: 'tipologia',
    header: 'Tipologia',
    enableSorting: true,
    size: 14,
  }),
  columnHelper.accessor('proprietarioNome', {
    id: 'proprietarioNome',
    header: 'Proprietario',
    enableSorting: true,
    size: 20,
  }),
  columnHelper.accessor('inquilinoNome', {
    id: 'inquilinoNome',
    header: 'Inquilino',
    enableSorting: true,
    size: 20,
  }),
  columnHelper.accessor('indirizzoImmobile', {
    id: 'indirizzoImmobile',
    header: 'Immobile',
    enableSorting: true,
    size: 22,
  }),
  columnHelper.accessor('stato', {
    id: 'stato',
    header: 'Stato',
    enableSorting: true,
    size: 12,
  }),
];

const HEADER_WIDTHS: Record<string, string> = {
  createdAt: COL_WIDTHS.data,
  tipologia: COL_WIDTHS.tipologia,
  proprietarioNome: COL_WIDTHS.proprietario,
  inquilinoNome: COL_WIDTHS.inquilino,
  indirizzoImmobile: COL_WIDTHS.indirizzo,
  stato: COL_WIDTHS.stato,
};

export function PraticheTable() {
  const { items } = useArchivio();
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;

  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  const showPerfWarning = rows.length > PERF_WARNING_THRESHOLD;

  return (
    <div className="space-y-2">
      {showPerfWarning ? (
        <div
          role="status"
          className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span>
            Stai visualizzando {rows.length} pratiche. Aggiungi filtri (data, tipologia, stato) per
            risultati più rapidi.
          </span>
        </div>
      ) : null}

      <div
        role="table"
        aria-rowcount={rows.length}
        aria-label="Elenco pratiche"
        className="rounded-md border"
      >
        {/* Header row */}
        <div role="row" className="flex items-center border-b bg-muted/30 px-4 py-2 text-xs font-semibold">
          {table.getHeaderGroups()[0]?.headers.map((header) => {
            const sortDir = header.column.getIsSorted();
            const ariaSort: 'ascending' | 'descending' | 'none' =
              sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none';
            const canSort = header.column.getCanSort();
            return (
              <button
                key={header.id}
                role="columnheader"
                aria-sort={ariaSort}
                type="button"
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                className="flex items-center gap-1 text-left hover:text-foreground"
                style={{ width: HEADER_WIDTHS[header.id] ?? 'auto' }}
              >
                <span>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </span>
                {canSort ? (
                  sortDir === 'asc' ? (
                    <ArrowUp className="h-3 w-3" aria-hidden="true" />
                  ) : sortDir === 'desc' ? (
                    <ArrowDown className="h-3 w-3" aria-hidden="true" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
                  )
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Virtualized rows */}
        <div
          ref={parentRef}
          className="max-h-[600px] overflow-auto"
          role="rowgroup"
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;
              return (
                <PraticaRow
                  key={row.id}
                  item={row.original}
                  // aria-rowindex is 1-based and includes the header row (1),
                  // so data rows start at 2.
                  rowIndex={virtualRow.index + 2}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
